import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import {
  JourneyEvent,
  getUserJourney,
} from "../../api/analyticsAdmin";
import { groupEventsBySession } from "../../helpers/analyticsPlus";
import {
  mergeJourneys,
  summarizeSession,
  SessionSummary,
} from "../../helpers/analyticsPlusJourney";
import { buildBusinessNameMap } from "../../helpers/eventHumanizer";
import { Loader } from "../Loader";
import { SessionList } from "./journey/SessionList";
import { SessionDetail } from "./journey/SessionDetail";

interface UserJourneyModalProps {
  distinctId: string | null;
  personId: string | null;
  distinctIds: string[];
  from: string;
  to: string;
  building: boolean;
  truncated: boolean;
  onClose: () => void;
}

const PAGE_SIZE = 500;

interface SessionEntry {
  summary: SessionSummary;
  events: JourneyEvent[];
}

const CopyId: React.FC<{ id: string; label?: string }> = ({ id, label }) => (
  <button
    onClick={() => navigator.clipboard?.writeText(id).catch(() => {})}
    title="Click to copy"
    className="font-mono text-xs text-gray-500 hover:text-gray-700"
  >
    {label ?? id}
  </button>
);

export const UserJourneyModal: React.FC<UserJourneyModalProps> = ({
  distinctId,
  personId,
  distinctIds,
  from,
  to,
  building,
  truncated,
  onClose,
}) => {
  const [events, setEvents] = useState<JourneyEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capped, setCapped] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  const idsKey = distinctIds.join("|");

  useEffect(() => {
    if (!distinctId || distinctIds.length === 0) return;
    let cancelled = false;
    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      setCapped(false);
      try {
        const responses = await Promise.all(
          distinctIds.map((id) =>
            getUserJourney({
              distinctId: id,
              from,
              to,
              pagination: { pageNumber: 1, pageSize: PAGE_SIZE },
            })
          )
        );
        if (cancelled) return;
        setCapped(responses.some((r) => r.events.length >= PAGE_SIZE));
        setEvents(mergeJourneys(responses.map((r) => r.events)));
      } catch {
        if (!cancelled) setError("Failed to load this person's journey.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAll();
    return () => {
      cancelled = true;
    };
    // idsKey captures the distinctIds set; from/to bound the range.
  }, [distinctId, idsKey, from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sessions newest-first; pair each group with its summary.
  const sessions: SessionEntry[] = useMemo(() => {
    const groups = groupEventsBySession(events);
    const entries = groups.map((group) => ({
      summary: summarizeSession(group),
      events: group.events,
    }));
    return entries.sort((a, b) =>
      a.summary.start < b.summary.start ? 1 : a.summary.start > b.summary.start ? -1 : 0
    );
  }, [events]);

  const businessNames = useMemo(() => buildBusinessNameMap(events), [events]);

  // Auto-select the most recent session when the data set changes.
  useEffect(() => {
    setSelectedSessionId((current) => {
      if (current && sessions.some((s) => s.summary.sessionId === current)) {
        return current;
      }
      return sessions.length > 0 ? sessions[0].summary.sessionId : null;
    });
  }, [sessions]);

  const selected =
    sessions.find((s) => s.summary.sessionId === selectedSessionId) ?? sessions[0] ?? null;

  const totalEvents = events.length;
  const primaryId = personId ?? distinctId ?? "";

  return (
    <Modal
      className="modal-content md:max-w-[90vw] md:w-[90vw]"
      overlayClassName="modal-overlay"
      isOpen={distinctId !== null}
      onRequestClose={onClose}
      contentLabel="User journey"
    >
      <div className="flex flex-col h-[85vh]">
        {/* Header */}
        <div className="flex justify-between items-start pb-4 border-b border-gray-200">
          <div>
            <h2 className="text-primary font-medium text-xl">User journey</h2>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
              <span className="text-xs text-gray-400">
                {personId ? "person" : "distinctId (unresolved)"}
              </span>
              <CopyId id={primaryId} />
              <span className="text-gray-300">·</span>
              <span className="text-xs text-gray-400">
                {distinctIds.length} identit{distinctIds.length === 1 ? "y" : "ies"}
              </span>
              <span className="text-gray-300">·</span>
              <span className="text-xs text-gray-400">
                {sessions.length} session{sessions.length === 1 ? "" : "s"} · {totalEvents} events
              </span>
              {building && (
                <span className="text-xs text-amber-600">· resolving person…</span>
              )}
            </div>
            {distinctIds.length > 1 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {distinctIds.map((id) => (
                  <span key={id} className="px-2 py-0.5 rounded bg-gray-100">
                    <CopyId id={id} label={`${id.slice(0, 8)}…`} />
                  </span>
                ))}
              </div>
            )}
            {truncated && (
              <div className="text-xs text-amber-600 mt-1">
                Person merge limited to the first 5000 users in range.
              </div>
            )}
            {capped && (
              <div className="text-xs text-amber-600 mt-1">
                Showing the first {PAGE_SIZE} events per identity.
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-sm"
          >
            Close
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 pt-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader className="w-8 h-8" />
            </div>
          ) : error ? (
            <div className="text-sm text-red-500">{error}</div>
          ) : sessions.length === 0 ? (
            <div className="text-sm text-gray-500">No events in this range.</div>
          ) : (
            <div className="grid grid-cols-[280px_1fr] gap-4 h-full min-h-0">
              <div className="border-r border-gray-200 overflow-y-auto">
                <SessionList
                  sessions={sessions.map((s) => s.summary)}
                  selectedId={selected?.summary.sessionId ?? null}
                  onSelect={setSelectedSessionId}
                />
              </div>
              <div className="min-h-0">
                {selected && (
                  <SessionDetail
                    summary={selected.summary}
                    events={selected.events}
                    businessNames={businessNames}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
